import { arrow, autoUpdate, computePosition, flip, offset, shift } from "@floating-ui/dom";
import type { Placement } from "@floating-ui/dom";

export interface FloatingPositionOptions {
    placement?: Placement;
    offset?: number;
    shiftPadding?: number;
    strategy?: "absolute" | "fixed";
    arrowElement?: HTMLElement | null;
}

export class FloatingPositionAdapter {
    static auto(
        reference: HTMLElement,
        floating: HTMLElement,
        options: FloatingPositionOptions = {},
    ): () => void {
        const update = (): void => {
            void FloatingPositionAdapter.place(reference, floating, options);
        };
        const cleanup = autoUpdate(reference, floating, update);
        update();
        return cleanup;
    }

    static async place(
        reference: HTMLElement,
        floating: HTMLElement,
        options: FloatingPositionOptions = {},
    ): Promise<void> {
        const middleware = [
            offset(options.offset ?? 8),
            flip(),
            shift({ padding: options.shiftPadding ?? 12 }),
        ];
        if (options.arrowElement) {
            middleware.push(arrow({ element: options.arrowElement, padding: options.shiftPadding ?? 8 }));
        }

        const { x, y, placement, middlewareData } = await computePosition(reference, floating, {
            strategy: options.strategy ?? "fixed",
            placement: options.placement ?? "bottom-end",
            middleware,
        });

        Object.assign(floating.style, {
            left: `${x}px`,
            top: `${y}px`,
        });
        floating.dataset.placement = placement;

        if (options.arrowElement) {
            const arrowData = middlewareData.arrow;
            const side = placement.split("-")[0] ?? "bottom";
            const staticSide: Record<string, string> = {
                top: "bottom",
                right: "left",
                bottom: "top",
                left: "right",
            };
            Object.assign(options.arrowElement.style, {
                left: arrowData?.x != null ? `${arrowData.x}px` : "",
                top: arrowData?.y != null ? `${arrowData.y}px` : "",
                right: "",
                bottom: "",
                [staticSide[side] ?? "top"]: "-0.4rem",
            });
        }
    }
}
